import { useCallback, useEffect, useState } from "react";
import { Download, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { money, fmtDate, fmtDateTime, PAYMENT_METHOD_LABELS, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const EXPENSE_METHODS = ["transfer", "card", "cash", "zelle", "other"];

function ExpenseModal({ open, onOpenChange, expense, categories, onSaved }) {
  const [form, setForm] = useState({ date: "", category: "", description: "", amount: "", method: "transfer" });
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(expense ? { date: expense.date, category: expense.category, description: expense.description || "", amount: String(expense.amount), method: expense.method || "transfer" }
      : { date: new Date().toISOString().slice(0, 10), category: categories[0] || "", description: "", amount: "", method: "transfer" });
  }, [open, expense, categories]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    try {
      const body = { ...form, amount: Number(form.amount), description: form.description.trim() || null };
      if (expense) await api.put(`/academy/expenses/${expense.id}`, body);
      else await api.post("/academy/expenses", body);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={ACADEMY.expenseModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{expense ? "Editar gasto" : "Nuevo gasto"}</DialogTitle>
          <DialogDescription className="sr-only">Gasto de la academia</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Fecha</Label>
              <Input type="date" value={form.date} onChange={set("date")} data-testid={ACADEMY.expenseDateInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} data-testid={ACADEMY.expenseAmountInput} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Categoría</Label>
              <select className={selectCls} value={form.category} onChange={set("category")} data-testid={ACADEMY.expenseCategorySelect}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Medio</Label>
              <select className={selectCls} value={form.method} onChange={set("method")}>
                {EXPENSE_METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m] || m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Detalle</Label>
            <Input value={form.description} onChange={set("description")} data-testid={ACADEMY.expenseDescriptionInput} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} data-testid={ACADEMY.expenseSaveButton}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, tone, testId }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-medium mt-1 ${tone || ""}`} data-testid={testId}>{money(value)}</p>
    </div>
  );
}

// Fix V (2026-10-13): finanzas de la academia: ingresos (entran solos), gastos,
// reembolsos para aprobar, cajas y el balance del mes descargable.
export default function AcademyFinancePage() {
  const { toast } = useToast();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [receivable, setReceivable] = useState(0);
  const [categories, setCategories] = useState([]);
  const [expenseModal, setExpenseModal] = useState({ open: false, expense: null });

  const load = useCallback(async () => {
    try {
      const [s, p, r] = await Promise.all([
        api.get("/academy/finance/summary", { params: { month } }), api.get("/academy/refunds", { params: { status: "pending" } }),
        api.get("/academy/finance/receivables"),
      ]);
      setData(s.data);
      setPending(p.data);
      setReceivable(r.data.balance);
    } catch (err) {
      toast({ title: "Error al cargar finanzas", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [month, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/academy/settings").then(({ data: s }) => setCategories(s.expense_categories || [])).catch(() => {}); }, []);

  const download = async () => {
    try {
      const res = await api.get("/academy/finance/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Balance_academia_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "No se pudo descargar", variant: "destructive" });
    }
  };

  const decide = async (r, approve) => {
    try {
      await api.post(`/academy/refunds/${r.id}/${approve ? "approve" : "reject"}`, {});
      toast({ title: approve ? "Reembolso aprobado" : "Reembolso rechazado" });
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const removeExpense = async (e) => {
    try {
      await api.delete(`/academy/expenses/${e.id}`);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid={ACADEMY.financePage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Finanzas de la academia</h1>
          <p className="text-base text-muted-foreground mt-1">Los cobros entran solos. Los gastos se cargan acá.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid={ACADEMY.financeMonthInput} />
          <Button variant="outline" onClick={download} data-testid={ACADEMY.financeDownloadButton}><Download className="w-4 h-4" /> Balance del mes</Button>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="bg-[#F4ECDD] border border-[#E6D3AF] rounded-xl p-4 space-y-2" data-testid={ACADEMY.refundsPending}>
          <p className="font-medium text-[#8A6A2F]">Reembolsos para aprobar ({pending.length})</p>
          {pending.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 text-sm">
              <span><strong>{r.student_name}</strong> · {money(r.amount)} por {PAYMENT_METHOD_LABELS[r.method] || r.method} · {r.reason} <span className="text-xs text-muted-foreground">(pidió {r.requested_by_name})</span></span>
              <span className="flex gap-1">
                <Button size="sm" className={btnBrand} onClick={() => decide(r, true)} data-testid={`${ACADEMY.refundApprovePrefix}${r.id}`}><Check className="w-4 h-4" /> Aprobar</Button>
                <Button size="sm" variant="outline" onClick={() => decide(r, false)} data-testid={`${ACADEMY.refundRejectPrefix}${r.id}`}><X className="w-4 h-4" /> Rechazar</Button>
              </span>
            </div>
          ))}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Ingresos" value={data.income} testId={ACADEMY.financeIncome} />
            <Kpi label="Reembolsos" value={data.refunds} />
            <Kpi label="Gastos" value={data.expenses} testId={ACADEMY.financeExpenses} />
            <Kpi label="Resultado" value={data.result} tone={data.result >= 0 ? "text-[#5B7A54]" : "text-[#A34B49]"} testId={ACADEMY.financeResult} />
            <Kpi label="Falta cobrar (todas las inscripciones)" value={receivable} />
          </div>

          <Tabs defaultValue="income">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="income">Ingresos ({data.payments.length})</TabsTrigger>
              <TabsTrigger value="expenses" data-testid={ACADEMY.financeExpensesTab}>Gastos ({data.expense_list.length})</TabsTrigger>
              <TabsTrigger value="refunds">Reembolsos ({data.refund_list.length})</TabsTrigger>
              <TabsTrigger value="cash">Cajas ({data.cash_sessions.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="income" className="pt-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.income_by_method).map(([m, v]) => <span key={m} className="px-3 py-1 rounded-full bg-[#E9F3F3] text-sm">{PAYMENT_METHOD_LABELS[m] || m}: <strong>{money(v)}</strong></span>)}
              </div>
              <div className="bg-white border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                    <th className="text-left font-medium py-2 px-4">Fecha</th><th className="text-left font-medium py-2 px-3">Alumno</th>
                    <th className="text-left font-medium py-2 px-3 hidden md:table-cell">Curso</th><th className="text-left font-medium py-2 px-3">Medio</th>
                    <th className="text-left font-medium py-2 px-3 hidden lg:table-cell">Ejecutivo</th><th className="text-right font-medium py-2 px-4">USD</th>
                  </tr></thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id} className="border-t border-border">
                        <td className="py-2 px-4">{fmtDate(p.paid_at)}</td><td className="py-2 px-3">{p.student_name}{p.is_reservation ? " · reserva" : ""}</td>
                        <td className="py-2 px-3 hidden md:table-cell">{p.course_name}</td><td className="py-2 px-3">{PAYMENT_METHOD_LABELS[p.method] || p.method}</td>
                        <td className="py-2 px-3 hidden lg:table-cell">{p.executive_name || "—"}</td><td className="py-2 px-4 text-right font-medium">{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            <TabsContent value="expenses" className="pt-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.expenses_by_category).map(([k, v]) => <span key={k} className="px-3 py-1 rounded-full bg-[#E9F3F3] text-sm">{k}: <strong>{money(v)}</strong></span>)}
                </div>
                <Button className={btnBrand} onClick={() => setExpenseModal({ open: true, expense: null })} data-testid={ACADEMY.expenseAddButton}><Plus className="w-4 h-4" /> Nuevo gasto</Button>
              </div>
              <div className="bg-white border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm" data-testid={ACADEMY.expensesTable}>
                  <tbody>
                    {data.expense_list.length === 0 && <tr><td className="py-4 px-4 text-muted-foreground">Sin gastos este mes.</td></tr>}
                    {data.expense_list.map((e) => (
                      <tr key={e.id} className="border-t border-border first:border-0">
                        <td className="py-2 px-4">{fmtDate(e.date)}</td><td className="py-2 px-3">{e.category}</td><td className="py-2 px-3 text-muted-foreground">{e.description}</td>
                        <td className="py-2 px-3">{PAYMENT_METHOD_LABELS[e.method] || e.method}{e.cash_session_id ? " (caja)" : ""}</td>
                        <td className="py-2 px-3 text-right font-medium">{money(e.amount)}</td>
                        <td className="py-2 px-4 text-right whitespace-nowrap">
                          {!e.cash_session_id && <>
                            <Button variant="ghost" size="icon" onClick={() => setExpenseModal({ open: true, expense: e })}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => removeExpense(e)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            <TabsContent value="refunds" className="pt-4">
              <div className="bg-white border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {data.refund_list.length === 0 && <tr><td className="py-4 px-4 text-muted-foreground">Sin reembolsos aprobados este mes.</td></tr>}
                    {data.refund_list.map((r) => (
                      <tr key={r.id} className="border-t border-border first:border-0">
                        <td className="py-2 px-4">{fmtDate(r.decided_local)}</td><td className="py-2 px-3">{r.student_name}</td><td className="py-2 px-3 text-muted-foreground">{r.reason}</td>
                        <td className="py-2 px-3">Aprobó {r.decided_by_name}</td><td className="py-2 px-4 text-right font-medium">{money(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            <TabsContent value="cash" className="pt-4">
              <div className="bg-white border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                    <th className="text-left font-medium py-2 px-4">Apertura</th><th className="text-left font-medium py-2 px-3">Abrió / cerró</th>
                    <th className="text-right font-medium py-2 px-3">Esperado</th><th className="text-right font-medium py-2 px-3">Contado</th>
                    <th className="text-right font-medium py-2 px-3">Diferencia</th><th className="text-left font-medium py-2 px-4">Nota</th>
                  </tr></thead>
                  <tbody>
                    {data.cash_sessions.map((s) => (
                      <tr key={s.id} className="border-t border-border">
                        <td className="py-2 px-4">{fmtDateTime(s.opened_at)}</td><td className="py-2 px-3">{s.opened_by_name} / {s.closed_by_name || "abierta"}</td>
                        <td className="py-2 px-3 text-right">{s.expected_amount != null ? money(s.expected_amount) : "—"}</td>
                        <td className="py-2 px-3 text-right">{s.counted_amount != null ? money(s.counted_amount) : "—"}</td>
                        <td className={`py-2 px-3 text-right ${s.difference ? "text-[#A34B49]" : ""}`}>{s.difference != null ? money(s.difference) : "—"}</td>
                        <td className="py-2 px-4 text-muted-foreground">{s.difference_note || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
      <ExpenseModal open={expenseModal.open} onOpenChange={(o) => setExpenseModal((m) => ({ ...m, open: o }))} expense={expenseModal.expense} categories={categories} onSaved={() => { toast({ title: "Gasto guardado" }); load(); }} />
    </div>
  );
}
